import { elementController } from '@public-pages/runtime-core';
import { ScrollByOptions } from './types';

elementController('app-slider', ({ root, query, queryAll }) => {
  const slidesTrailHolder = query('.app-slider__trail__holder') as HTMLElement;
  const slidesTrail = query('.app-slider__trail') as HTMLElement;
  const slides = queryAll('.app-slider__slide');

  const navigationHolder = query('.app-slider__navigation') as HTMLSpanElement;
  const indexHolder = queryAll('.app-slider__index') as HTMLSpanElement[];
  const dotsContainer = query('.app-slider__dots') as HTMLElement;

  const playPauseBtn = query('.app-slider__timer__text') as HTMLElement;
  const playPauseIcon = query('.app-slider__timer__icon') as HTMLImageElement;
  const progressRing = query('.app-slider__timer__ring') as unknown as SVGCircleElement;

  const sliderContent = query('.app-slider__content') as HTMLElement;

  const arrowDisableClass = 'arrow-btn--disabled';
  const gap = root.dataset?.gap ? Number(root.dataset?.gap) : 0;

  const rewind = root.hasAttribute('data-rewind');
  const autoplay = root.hasAttribute('data-autoplay');

  const rightBtn = queryAll('[data-control="btn-right"]') as HTMLButtonElement[];
  const leftBtn = queryAll('[data-control="btn-left"]') as HTMLButtonElement[];

  const itemsPerView = root.dataset?.itemsPerView;
  const itemsPerViewTablet = root.dataset?.itemsPerViewTablet;
  const itemsPerViewDesk = root.dataset?.itemsPerViewDesk;

  const scrollBy = root.dataset?.scrollby as ScrollByOptions;
  const scrollByTablet = (root.dataset?.scrollbyTablet || 'single') as ScrollByOptions;
  const scrollByDesk = (root.dataset?.scrollbyDesk || 'single') as ScrollByOptions;

  const fillSpace = root.hasAttribute('data-fill-space');

  let usedItemsPerView = itemsPerView;
  let usedScrollBy = scrollBy;
  let sliderScrollDistance = getScrollByDistance(usedScrollBy);
  let currentPage = 1;
  let numberOfPages = 0;
  let autoSlideInterval: number | null = null;
  let isPaused = false;
  let slideDuration = 5000;
  let ringRadius = 12;
  let circumference = 2 * Math.PI * ringRadius;

  if (slides.length == Number(itemsPerViewDesk)) {
    sliderContent.classList.add('hide-navigation');
  }

  window.addEventListener('resize', handleResize);
  window.addEventListener('render-slider', handleResize);
  slidesTrail.addEventListener('scroll', handleNavigationArrows);

  rightBtn.forEach((btn) => btn.addEventListener('click', handleScrollRight));
  leftBtn.forEach((btn) => btn.addEventListener('click', handleScrollLeft));

  progressRing.style.strokeDasharray = `${circumference} ${circumference}`;
  progressRing.style.strokeDashoffset = `${circumference}`;

  root.addEventListener('mouseenter', pauseAutoSlide);
  root.addEventListener('mouseleave', () => {
    if (autoplay && isPaused) {
      startAutoSlide();
    }
  });

  handleNavigationArrows();
  getUsedItemsPerView();
  renderIndex();
  createDots();

  if (autoplay) {
    startAutoSlide();
  }

  function getScrollByDistance(scrollType: ScrollByOptions): number {
    if (scrollType === 'single') return 10;

    const slidesHolderSize = slidesTrailHolder.clientWidth;
    return slidesHolderSize;
  }

  function handleScrollRight() {
    if (currentPage < numberOfPages) {
      scrollSliderTo(sliderScrollDistance);
    } else if (rewind) {
      slidesTrail.scrollTo({ left: 0, behavior: 'smooth' });
    }

    handleReset();
  }

  function handleScrollLeft() {
    if (currentPage > 1) {
      scrollSliderTo(-sliderScrollDistance);
    } else if (rewind) {
      slidesTrail.scrollTo({
        left: slidesTrail.scrollWidth,
        behavior: 'smooth',
      });
    }

    handleReset();
  }

  function scrollSliderTo(distance: number) {
    slidesTrail.scrollBy({ left: distance, behavior: 'smooth' });
  }

  function scrollToSlide(index: number) {
    const slideWidth = getScrollByDistance(scrollBy);
    slidesTrail.scrollTo({ left: index * slideWidth, behavior: 'smooth' });

    handleReset();
  }

  function handleNavigationArrows() {
    const sliderScrollDistance = getScrollByDistance(scrollBy) + gap;
    const slidesPosition = slidesTrail.scrollLeft + slidesTrail.clientWidth + 1;
    const slideSize = sliderScrollDistance - gap;

    currentPage = Math.floor(slidesPosition / slideSize);
    renderIndex();
    updateDots();

    if (!rewind) {
      const isLeftArrowDisabled = leftBtn.some(getIsArrowDisabled);
      const isRightArrowDisabled = rightBtn.some(getIsArrowDisabled);

      const reachedStart = slidesTrail.scrollLeft === 0;
      const reachedEnd = slidesPosition >= slidesTrail.scrollWidth;

      if (numberOfPages <= 1 || reachedStart) {
        leftBtn.forEach(disableArrow);
      } else if (isLeftArrowDisabled) {
        leftBtn.forEach(enableArrow);
      }

      if (numberOfPages <= 1 || reachedEnd) {
        rightBtn.forEach(disableArrow);
      } else if (isRightArrowDisabled) {
        rightBtn.forEach(enableArrow);
      }
    }
  }

  function getUsedItemsPerView() {
    if (window.innerWidth < 768) {
      usedItemsPerView = itemsPerView;
      usedScrollBy = scrollBy;
    } else if (window.innerWidth >= 768 && window.innerWidth < 991) {
      usedItemsPerView = itemsPerViewTablet;
      usedScrollBy = scrollByTablet;
    } else {
      usedItemsPerView = itemsPerViewDesk;
      usedScrollBy = scrollByDesk;
    }

    sliderScrollDistance = getScrollByDistance(usedScrollBy) + gap;
    renderIndex();

    if (fillSpace) {
      const emptySlides = queryAll('.app-slider__slide--empty');
      emptySlides.forEach((slide) => slide.remove());

      const numberOfSlides = slides?.length || 0;
      const numberOfSlidesPerPage = Number(usedItemsPerView || '0');
      const numberOfSpaces = numberOfSlidesPerPage - (numberOfSlides % numberOfSlidesPerPage);

      Array(numberOfSpaces).fill('').forEach(() => {
        const newSlide = slides?.[0].cloneNode(true) as HTMLElement;
        newSlide.classList.add('app-slider__slide--empty');
        newSlide.style.visibility = 'hidden';
        newSlide.children[0].removeChild(newSlide.children[0].children[0]);
        slidesTrail.appendChild(newSlide);
      });
    }
  }

  playPauseBtn.addEventListener('click', () => {
    if (isPaused) {
      startAutoSlide();
    } else {
      pauseAutoSlide();
    }
  });

  function handleResize() {
    currentPage = 1;
    getUsedItemsPerView();

    slidesTrail.scrollTo({ left: 0, behavior: 'smooth' });

    if (!rewind) {
      rightBtn.forEach((btn) => enableArrow(btn));
      leftBtn.forEach((btn) => disableArrow(btn));
    }
  }

  function startProgressAnimation() {
    progressRing.style.transition = `stroke-dashoffset ${slideDuration}ms linear`;
    progressRing.style.strokeDashoffset = '0';
    progressRing.style.display = 'block';
  }

  function stopProgressAnimation() {
    progressRing.style.transition = 'none';
    progressRing.style.strokeDashoffset = `${circumference}`;
  }

  function resetProgressAnimation() {
    progressRing.style.transition = 'none';
    progressRing.style.strokeDashoffset = `${circumference}`;
    setTimeout(() => {
      startProgressAnimation();
    }, 10);
  }

  function startAutoSlide() {
    isPaused = false;
    playPauseBtn.textContent = 'Pausar';
    playPauseIcon.src = '/images/icons/pause.svg';

    autoSlideInterval = window.setInterval(() => {
      handleScrollRight();
      resetProgressAnimation();
    }, slideDuration);

    startProgressAnimation();
  }

  function pauseAutoSlide() {
    isPaused = true;
    playPauseBtn.textContent = 'Reproduzir';
    playPauseIcon.src = '/images/icons/play.svg';

    if (autoSlideInterval) {
      window.clearInterval(autoSlideInterval);
      autoSlideInterval = null;
    }

    stopProgressAnimation();
  }

  function handleReset() {
    if (!isPaused) {
      pauseAutoSlide();
      setTimeout(() => {
        if (autoplay) {
          startAutoSlide();
        }
      }, 10);
    }
  }

  function renderIndex() {
    const numberOfSlides = slides?.length || 0;
    const numberOfSlidesPerPage = Number(usedItemsPerView || '0');

    if (numberOfSlides && numberOfSlidesPerPage) {
      numberOfPages = Math.ceil(numberOfSlides / numberOfSlidesPerPage);
    } else {
      numberOfPages = 0;
    }

    if (numberOfPages == 1) {
      navigationHolder.style.display = 'none';
    }

    indexHolder.forEach(
      (holder) => (holder.innerText = `${currentPage} de ${numberOfPages}`)
    );
  }

  function createDots() {
    const numberOfSlides = slides.length;
    dotsContainer.innerHTML = '';

    for (let i = 0; i < numberOfSlides; i++) {
      const dot = document.createElement('span');
      dot.classList.add('app-slider__dot');
      dot.addEventListener('click', () => {
        scrollToSlide(i);
      });
      dotsContainer.appendChild(dot);
    }
    updateDots();
  }

  function updateDots() {
    const dots = queryAll('.app-slider__dot');
    dots.forEach((dot, index) => {
      dot.classList.toggle('active', index === currentPage - 1);
    });
  }

  function enableArrow(arrowBtn: HTMLButtonElement) {
    arrowBtn.classList.remove(arrowDisableClass);
  }

  function disableArrow(arrowBtn: HTMLButtonElement) {
    arrowBtn.classList.add(arrowDisableClass);
  }

  function getIsArrowDisabled(arrowBtn: HTMLButtonElement) {
    return arrowBtn.classList.contains(arrowDisableClass);
  }
});
